import {
  DatePicker,
  DatePickerChangeEvent,
  DateTimePicker,
  DateTimePickerChangeEvent
} from '@progress/kendo-react-dateinputs';
import { RadioButtonChangeEvent } from '@progress/kendo-react-inputs';
import { useEffect, useRef, useState } from 'react';
import {
  useDocumentsContext,
  useGlobalizationContext,
  useSearchContext,
  useTranslation
} from '@cs-cms/state-management';
import { GlobalFilterState } from './GlobalFilterState';
import {
  buildGroupedDateRangeFilter,
  getDateFilterFieldNames,
  getEndOfToday,
  getFieldDisplayName,
  getGroupedDateRangeValues,
  getInitialFieldSelected,
  GroupedDatePickerId,
  isGroupedDatePickerId,
  PICKER_END_ID,
  PICKER_START_ID,
  parseFilterDateValue,
  removeDateFiltersFromRequest,
  removeFieldFromCompositeFilter,
  shouldRetainFilterOnPickerChange,
  toGroupedDatePickerValue
} from './groupedDateGlobalFilterUtils';

interface UseGroupedDateGlobalFilterParams {
  dateFiltersState: GlobalFilterState[];
}

export function useGroupedDateGlobalFilter(params: UseGroupedDateGlobalFilterParams): {
  fieldSelected: GlobalFilterState | undefined;
  fieldDisplayName: string;
  translate: (key: string) => string;
  currentValueStart: Date | null;
  currentValueEnd: Date | null | undefined;
  todayEndOfTheDay: Date;
  startPickerOpen: boolean;
  endPickerOpen: boolean;
  handleFieldChange: (event: RadioButtonChangeEvent) => void;
  handleChange: (event: DateTimePickerChangeEvent | DatePickerChangeEvent) => void;
  handlePopupOpen: (pickerId: string) => void;
  handlePopupClose: (pickerId: string) => void;
  dateTimeStartRef: React.RefObject<DateTimePicker | null>;
  dateTimeEndRef: React.RefObject<DateTimePicker | null>;
  dateStartRef: React.RefObject<DatePicker | null>;
  dateEndRef: React.RefObject<DatePicker | null>;
} {
  const { dateFiltersState } = params;
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const {
    queryRequest,
    setQueryRequest,
    compositeFilterDescriptor,
    setCompositeFilterDescriptor,
    requestedGlobalDateFieldName,
    setRequestedGlobalDateFieldName
  } = useSearchContext();
  const { setSelectedDocuments } = useDocumentsContext();
  const [fieldSelected, setFieldSelected] = useState<GlobalFilterState | undefined>(() =>
    getInitialFieldSelected(dateFiltersState)
  );
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);
  const dateTimeStartRef = useRef<DateTimePicker | null>(null);
  const dateTimeEndRef = useRef<DateTimePicker | null>(null);
  const dateStartRef = useRef<DatePicker | null>(null);
  const dateEndRef = useRef<DatePicker | null>(null);

  useEffect(() => {
    if (!requestedGlobalDateFieldName) {
      return;
    }

    const matchingField = dateFiltersState.find(filter => filter.name === requestedGlobalDateFieldName);
    if (matchingField) {
      setFieldSelected(matchingField);
    }
    setRequestedGlobalDateFieldName(undefined);
  }, [requestedGlobalDateFieldName, dateFiltersState, setRequestedGlobalDateFieldName]);

  const clearSelectedDocuments = (): void => {
    if (setSelectedDocuments) {
      setSelectedDocuments([]);
    }
  };

  const handleFieldChange = (event: RadioButtonChangeEvent): void => {
    const newFieldSelected = dateFiltersState.find(filter => filter.id === event.value);
    setFieldSelected(newFieldSelected);

    if (!queryRequest || !setQueryRequest || !setCompositeFilterDescriptor || !fieldSelected) {
      return;
    }

    const newFilters = removeDateFiltersFromRequest(
      queryRequest.filters,
      getDateFilterFieldNames(dateFiltersState)
    );
    setQueryRequest({
      ...queryRequest,
      filters: newFilters
    });
    clearSelectedDocuments();
  };

  const openPickerOnChange = (pickerId: GroupedDatePickerId): void => {
    if (pickerId === PICKER_START_ID) {
      setStartPickerOpen(true);
      return;
    }
    setEndPickerOpen(true);
  };

  const applyPickerChange = (pickerId: GroupedDatePickerId, pickerValue: Date | null): void => {
    if (!queryRequest || !setQueryRequest || !setCompositeFilterDescriptor || !fieldSelected) {
      return;
    }

    const retainedFilters =
      queryRequest.filters?.filter(filter =>
        shouldRetainFilterOnPickerChange(filter, fieldSelected.name, pickerId)
      ) ?? [];

    if (compositeFilterDescriptor) {
      setCompositeFilterDescriptor(
        removeFieldFromCompositeFilter(compositeFilterDescriptor, fieldSelected.name)
      );
    }

    const newFilters = [...retainedFilters];
    if (pickerValue) {
      const rangeFilter = buildGroupedDateRangeFilter(fieldSelected, pickerId, pickerValue);
      if (rangeFilter) {
        newFilters.push(rangeFilter);
      }
    }

    setQueryRequest({
      ...queryRequest,
      filters: newFilters
    });
    clearSelectedDocuments();
  };

  const handleChange = (event: DateTimePickerChangeEvent | DatePickerChangeEvent): void => {
    const pickerId = event.target.props?.id;
    if (!isGroupedDatePickerId(pickerId)) {
      return;
    }

    openPickerOnChange(pickerId);
    applyPickerChange(pickerId, toGroupedDatePickerValue(event.value));
  };

  const handlePopupOpen = (pickerId: string): void => {
    const isStartPicker = pickerId === PICKER_START_ID;
    setStartPickerOpen(isStartPicker);
    setEndPickerOpen(!isStartPicker);
  };

  const handlePopupClose = (pickerId: string): void => {
    if (pickerId === PICKER_START_ID) {
      setStartPickerOpen(false);
      return;
    }
    setEndPickerOpen(false);
  };

  const rangeValues = fieldSelected
    ? getGroupedDateRangeValues(queryRequest?.filters, fieldSelected.name)
    : undefined;

  return {
    fieldSelected,
    fieldDisplayName: fieldSelected ? getFieldDisplayName(dateFiltersState, fieldSelected) : '',
    translate,
    currentValueStart: parseFilterDateValue(rangeValues?.start),
    currentValueEnd: parseFilterDateValue(rangeValues?.end),
    todayEndOfTheDay: getEndOfToday(),
    startPickerOpen,
    endPickerOpen,
    handleFieldChange,
    handleChange,
    handlePopupOpen,
    handlePopupClose,
    dateTimeStartRef,
    dateTimeEndRef,
    dateStartRef,
    dateEndRef
  };
}
